// frontend/src/pages/SearchAnalytics.jsx
// Staff-only: what merchants search for in the command palette.
//
// Data source: MerchantEvent rows with event_type='command_palette_search'.
// Backend aggregates at /api/analytics/insights/search-insights/.

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
} from '@mui/material';
import PageBanner from '../components/common/PageBanner';
import FilterChips from '../components/common/FilterChips';
import FilterRow from '../components/common/FilterRow';
import { AgGridReact } from 'ag-grid-react';
import Tile from '../components/layout/Tile';
import ReportKpiStrip from '../components/reports/ReportKpiStrip';
import { sessionApi } from '../services/SessionApiService';
import { apiErrorFrom } from '../utils/parseApiError';
import { useAgGridTheme } from '../utils/agGridTheme';
import { useColumnTypes } from '../hooks/useColumnTypes';
import { useGridState } from '../hooks/useGridState';
import { useFormatters } from '../hooks/useFormatters';
import { defaultColDef, defaultGridProps, copyOnlyContextMenuItems } from '../utils/gridDefaults';
import '../styles/pages.css';
import '../styles/grid-page.css';

const DAYS_OPTIONS = [7, 30, 90, 365];
const LIMIT_OPTIONS = [50, 250, 1000];
const LANGUAGES = [
  { code: '', labelKey: 'searchAnalytics.filters.allLanguages' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'pl', label: 'Polski' },
];

// Code → display label for the Language grid column. Unknown codes fall back to
// the raw code so a new locale is never hidden.
const LANGUAGE_LABELS = Object.fromEntries(
  LANGUAGES.filter((l) => l.code && l.label).map((l) => [l.code, l.label]),
);

const GRID_HEIGHT = 360;

function SearchAnalytics() {
  const { t } = useTranslation();
  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const { formatNumber, formatDateTime } = useFormatters();
  const topQueriesGrid = useGridState('search_analytics_top_queries');
  const zeroResultsGrid = useGridState('search_analytics_zero_results');
  const topPathsGrid = useGridState('search_analytics_top_paths');
  const byLanguageGrid = useGridState('search_analytics_by_language');

  const [days, setDays] = useState(30);
  const [language, setLanguage] = useState('');
  const [limit, setLimit] = useState(50);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ days: String(days), limit: String(limit) });
        if (language) params.set('language', language);
        const res = await sessionApi.get(`/api/analytics/insights/search-insights/?${params}`);
        if (!res.ok) throw await apiErrorFrom(res, 'GET /api/analytics/insights/search-insights/');
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) {
          console.error('searchAnalytics load:', e);
          setError(t('searchAnalytics.loadFailed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [days, language, limit, t]);

  const kpiItems = useMemo(() => {
    if (!data) return [];
    const s = data.summary;
    return [
      {
        key: 'total',
        label: t('searchAnalytics.kpi.totalSearches'),
        value: formatNumber(s.total_searches, 0),
        tooltip: t('searchAnalytics.kpi.totalSearchesTooltip'),
      },
      {
        key: 'unique',
        label: t('searchAnalytics.kpi.uniqueQueries'),
        value: formatNumber(s.unique_queries, 0),
        tooltip: t('searchAnalytics.kpi.uniqueQueriesTooltip'),
      },
      {
        key: 'zero',
        label: t('searchAnalytics.kpi.zeroResultRate'),
        value: formatNumber(s.zero_result_rate, 1),
        suffix: '%',
        highlight: s.zero_result_rate >= 20 ? 'error' : (s.zero_result_rate >= 10 ? 'warning' : undefined),
        tooltip: t('searchAnalytics.kpi.zeroResultRateTooltip'),
      },
      {
        key: 'ctr',
        label: t('searchAnalytics.kpi.clickThroughRate'),
        value: formatNumber(s.click_through_rate, 1),
        suffix: '%',
        highlight: s.click_through_rate >= 60 ? 'success' : undefined,
        tooltip: t('searchAnalytics.kpi.clickThroughRateTooltip'),
      },
    ];
  }, [data, formatNumber, t]);

  const topQueryColumns = useMemo(() => [
    { field: 'query', headerName: t('searchAnalytics.columns.query'), flex: 2, minWidth: 220 },
    { field: 'search_count', headerName: t('searchAnalytics.columns.searches'), type: 'numberColumn', width: 120 },
    { field: 'clicked_count', headerName: t('searchAnalytics.columns.clicked'), type: 'numberColumn', width: 120 },
    {
      field: 'ctr_pct',
      headerName: t('searchAnalytics.columns.ctr'),
      type: 'decimalColumn',
      width: 110,
      valueFormatter: (p) => (p.value == null ? '—' : `${formatNumber(p.value, 1)}%`),
    },
    {
      field: 'zero_result_count',
      headerName: t('searchAnalytics.columns.zeroResults'),
      type: 'numberColumn',
      width: 130,
      cellClassRules: { 'ag-cell-attention-warning': (p) => p.value > 0 },
    },
    { field: 'top_path', headerName: t('searchAnalytics.columns.topTarget'), flex: 1, minWidth: 180 },
  ], [t, formatNumber]);

  const zeroResultColumns = useMemo(() => [
    { field: 'query', headerName: t('searchAnalytics.columns.query'), flex: 2, minWidth: 220 },
    { field: 'count', headerName: t('searchAnalytics.columns.searches'), type: 'numberColumn', width: 140 },
    {
      field: 'last_seen',
      headerName: t('searchAnalytics.columns.lastSeen'),
      type: 'dateTimeColumn',
      width: 200,
      valueFormatter: (p) => (p.value ? formatDateTime(p.value) : '—'),
    },
  ], [t, formatDateTime]);

  const pathColumns = useMemo(() => [
    { field: 'path', headerName: t('searchAnalytics.columns.path'), flex: 2, minWidth: 260 },
    { field: 'count', headerName: t('searchAnalytics.columns.clicks'), type: 'numberColumn', width: 140 },
  ], [t]);

  const langRows = useMemo(() => {
    if (!data?.by_language) return [];
    // Normalize null/empty to 'unknown' for display
    return data.by_language.map(r => ({
      language: r.language || '—',
      count: r.count,
    }));
  }, [data]);

  const daysOptions = useMemo(() => DAYS_OPTIONS.map((d) => ({
    value: d,
    label: t('searchAnalytics.filters.days', { count: d }),
  })), [t]);

  const langColumns = useMemo(() => [
    { field: 'language', headerName: t('searchAnalytics.columns.language'), width: 160,
      valueFormatter: (p) => LANGUAGE_LABELS[p.value] || p.value },
    { field: 'count', headerName: t('searchAnalytics.columns.searches'), type: 'numberColumn', width: 140 },
  ], [t]);

  return (
    <div className="page-container">
      <FilterRow actions={loading && <CircularProgress size={20} />}>
        <FilterChips options={daysOptions} value={days} onChange={setDays} />

        <FormControl size="small" className="filter-row-slot">
          <InputLabel>{t('searchAnalytics.filters.language')}</InputLabel>
          <Select
            value={language}
            label={t('searchAnalytics.filters.language')}
            onChange={(e) => setLanguage(e.target.value)}
          >
            {LANGUAGES.map(l => (
              <MenuItem key={l.code || 'all'} value={l.code}>
                {l.labelKey ? t(l.labelKey) : l.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" className="filter-row-slot">
          <InputLabel>{t('searchAnalytics.filters.rows')}</InputLabel>
          <Select
            value={limit}
            label={t('searchAnalytics.filters.rows')}
            onChange={(e) => setLimit(Number(e.target.value))}
          >
            {LIMIT_OPTIONS.map(n => (
              <MenuItem key={n} value={n}>{n}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </FilterRow>

      <PageBanner severity="error" messages={[error]} />

      <ReportKpiStrip
        title={t('searchAnalytics.kpi.title')}
        items={kpiItems}
        loading={loading}
        storageKey="search_analytics_kpi"
      />

      <Tile title={t('searchAnalytics.topQueries.title')} subtitle={t('searchAnalytics.topQueries.subtitle')}>
        <div style={{ height: GRID_HEIGHT, width: '100%' }}>
          <AgGridReact
            theme={gridTheme}
            {...defaultGridProps}
            defaultColDef={defaultColDef}
            columnTypes={columnTypes}
            rowData={data?.top_queries || []}
            columnDefs={topQueryColumns}
            getContextMenuItems={copyOnlyContextMenuItems}
            {...topQueriesGrid}
          />
        </div>
      </Tile>

      <Tile
        title={t('searchAnalytics.zeroResults.title')}
        subtitle={t('searchAnalytics.zeroResults.subtitle')}
        infoText={t('searchAnalytics.zeroResults.infoText')}
      >
        <div style={{ height: GRID_HEIGHT, width: '100%' }}>
          <AgGridReact
            theme={gridTheme}
            {...defaultGridProps}
            defaultColDef={defaultColDef}
            columnTypes={columnTypes}
            rowData={data?.zero_result_queries || []}
            columnDefs={zeroResultColumns}
            getContextMenuItems={copyOnlyContextMenuItems}
            {...zeroResultsGrid}
          />
        </div>
      </Tile>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2 }}>
        <Tile title={t('searchAnalytics.topPaths.title')} subtitle={t('searchAnalytics.topPaths.subtitle')}>
          <div style={{ height: GRID_HEIGHT, width: '100%' }}>
            <AgGridReact
              theme={gridTheme}
              {...defaultGridProps}
              defaultColDef={defaultColDef}
              columnTypes={columnTypes}
              rowData={data?.top_paths || []}
              columnDefs={pathColumns}
              getContextMenuItems={copyOnlyContextMenuItems}
              {...topPathsGrid}
            />
          </div>
        </Tile>

        <Tile title={t('searchAnalytics.byLanguage.title')} subtitle={t('searchAnalytics.byLanguage.subtitle')}>
          <div style={{ height: GRID_HEIGHT, width: '100%' }}>
            <AgGridReact
              theme={gridTheme}
              {...defaultGridProps}
              defaultColDef={defaultColDef}
              columnTypes={columnTypes}
              rowData={langRows}
              columnDefs={langColumns}
              getContextMenuItems={copyOnlyContextMenuItems}
              {...byLanguageGrid}
            />
          </div>
        </Tile>
      </Box>
    </div>
  );
}

export default SearchAnalytics;
